// @givmo/app-admin — the company-suite waffle, mounted at the top-left of this portal's chrome.
//
// EP-SUITE3 gives every Givmo company-suite portal the SAME app list: the console's nine-dot popup
// and this one pop the same squares (shell contract v3: "My Givmo Apps" — the apps this person can
// open, the Admin Console tile last for a holder — then the rest of what the organization's class is
// offered), so a person can move between their company's apps without going back out to a landing
// page.
//
// ⚠ IT RENDERS NO DOM WHILE THE SWITCH IS OFF, and that is the whole gate. With
// `VITE_COMPANY_CONSOLE_ENABLED` unset this component returns `null` — not a hidden button, not a
// disabled one — so the employer portal is byte-identical to before. The switch is the SAME variable
// the console reads, because the waffle is an entry point INTO the console: revealing it while the
// console is dark in the same environment would advertise a destination that renders a
// "not available here" notice. `app-shell.test.tsx` pins both directions.
//
// ⚠ IT ALSO FILTERS BY THIS ADMIN'S STANDING (PR-2). The read is `GET /admin/company/me`, which this
// portal already mounts, so the waffle costs no extra request — and that route is NOT gated on
// `COMPANY_CONSOLE_ENABLED`, so the filter is correct even where the console's own routes 404.
//
// ⚠ IT IS IN THE TOP BAR, NOT THE SIDEBAR. The sidebar's `NAV_ITEMS` is a shipped e2e contract with a
// byte-identity pin on it; the waffle is chrome for moving between APPS, while the sidebar is
// navigation WITHIN this one. Keeping them apart is what lets this land without touching that pin.

import { AppSwitcher } from '@givmo/design-system';
import { isCompanyConsoleEnabled } from '../lib/env';
import { usePortalModules } from '../modules/use-portal-modules';

export function AppSwitcherSlot() {
  // Hooks run before the gates so the hook order is stable across a flag change within one session
  // (the flag is build-time, so this is discipline rather than a live concern).
  const { mine, more, hasOtherApps, isPending } = usePortalModules();
  if (!isCompanyConsoleEnabled()) return null;
  // ⚠ THIRD GATE (EP-SUITE3 PR-2): nothing until the standing read settles. The rule answers
  // permissively while it has no standing to apply, so rendering during that window can show a waffle
  // that DISAPPEARS a beat later for an admin who holds nothing else — and a control that vanishes
  // under someone reads as a bug, while one that fills in reads as loading. On error the read settles
  // permissive, so a failed request never hides an admin's apps.
  if (isPending) return null;
  // ⚠ SECOND GATE: nowhere to go ⇒ no switcher. Module URLs are undefaulted env-config, so a build
  // given the flag but no other destination resolves to a single square — this portal itself — and a
  // switcher that opens onto only the app you are in is a control that does nothing. Rendering
  // nothing is the honest state; the fix is to set the variables, never to guess a host. (The
  // console's own waffle is ungated: there the popup's request-able section is always real.)
  if (!hasOtherApps) return null;
  return <AppSwitcher mine={mine} more={more} />;
}
